import { FC } from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { ReactComponent as CheckmarkIcon } from '../../assets/icons/checkmark.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import AccessibleHiddenText from '../AccessibleHiddenText';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const Button = styled.button(({ theme }) => {
  const { color } = theme.new;

  return `
    position: relative;
    height: 3.125rem;
    width: 3.125rem;
    border-radius: 100px;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    background-color: ${color.neutral[3]};

    & > .completeContent,
    & > .hoverContent {
      display: none;
    }

    &:hover {
      background-color: ${color.neutral[6]};

      .hoverContent {
        display: flex;
      }
    }

    &.complete {
      background-color: ${color.brand[2].dark};

      & > .completeContent {
        display: block;
      }

      &:hover {
        background-color: ${color.brand[2].darker};

        & > .completeContent {
          display: none;
        }
      }
    }
  `;
});

const DotContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  gap: 4px;
  height: 0.5625rem;
`;

type TaskDotProps = {
  theme: DefaultTheme;
  programId: number;
};

const TaskDot = styled.div<TaskDotProps>(({ theme, programId }) => {
  const { color } = theme.new;

  return `
    width: 0.5625rem;
    height: 0.5625rem;
    background-color: ${color.program[programId]};
    border-radius: 100px;
  `;
});

const HoverWrapper = styled.span(({ theme }) => {
  const { color, type } = theme.new;

  return `
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    color: ${color.shade[1]};
    font-family: ${type.face};
    font-size: ${type.size[12]};
    font-weight: ${type.weight.regular};
    line-height: 1.125rem;

    svg {
      display: block;
      height: 100%;
      width: 100%;

      path {
        stoke: ${color.shade[1]};
      }
    }
  `;
});

const EyeWrapper = styled.span`
  display: block;
  width: 0.875rem;
  height: 0.875rem;
`;

const CheckmarkWrapper = styled.span(({ theme }) => {
  const { color } = theme.new;

  return `
    width: 1.0625rem;
    height: 0.75rem;
    display: block;

    svg {
      width: 100%;
      height: 100%;

      path {
        fill: ${color.shade[1]}
      }
    }
  `;
});

interface DateStatusProps {
  complete?: boolean;
  callback?: () => void;
  tasks: number[];
}

const DateStatus: FC<DateStatusProps> = ({ complete, callback, tasks }) => {
  const taskLoop = tasks.map((task) => {
    return <TaskDot key={`taskId-${task}`} programId={task} />;
  });

  return (
    <Container>
      <Button onClick={callback} className={complete ? 'complete' : ''}>
        <HoverWrapper className="hoverContent">
          <EyeWrapper>
            <EyeIcon />
          </EyeWrapper>
          View <AccessibleHiddenText>tasks</AccessibleHiddenText>
        </HoverWrapper>

        <CheckmarkWrapper className="completeContent">
          <CheckmarkIcon />
        </CheckmarkWrapper>
      </Button>

      <DotContainer>{taskLoop}</DotContainer>
    </Container>
  );
};

export default DateStatus;
